import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getMe } from '../api/client';
import type { UserMe } from '../api/types';
import { ApiClientError } from '../api/types';
import { motionStyles, statStyles, boxSizingStyles, responsiveStyles } from '../styles/lit-shared';
import { navigate } from '../router';

@customElement('dashboard-page')
export class DashboardPage extends LitElement {
  static styles = [
    boxSizingStyles,
    responsiveStyles,
    motionStyles,
    statStyles,
    css`
    :host {
      display: block;
    }

    .hero {
      position: relative;
      margin-bottom: var(--space-2xl);
      padding: var(--space-xl);
      border-radius: var(--radius-lg);
      background: var(--gradient-primary-soft);
      border: 1px solid var(--color-border-glow);
      overflow: hidden;
    }

    .hero::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -10%;
      width: 300px;
      height: 300px;
      border-radius: 50%;
      background: var(--glow-violet);
      filter: blur(50px);
      pointer-events: none;
    }

    .hero-content {
      position: relative;
      z-index: 1;
    }

    .hero-eyebrow {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      margin-bottom: var(--space-sm);
      padding: 0.25rem 0.75rem;
      border-radius: var(--radius-full);
      background: rgb(255 255 255 / 6%);
      border: 1px solid var(--color-border);
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--color-accent);
    }

    h1 {
      font-size: clamp(1.75rem, 4vw, 2.25rem);
      font-weight: 700;
      letter-spacing: -0.03em;
      margin-bottom: var(--space-xs);
      background: var(--gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .subtitle {
      color: var(--color-text-muted);
      font-size: 1rem;
      max-width: 36ch;
    }

    .credits-card {
      position: relative;
      padding: var(--space-lg);
      border-radius: var(--radius-lg);
      background: var(--color-surface);
      border: 1px solid var(--color-border-glow);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      overflow: hidden;
      margin-bottom: var(--space-2xl);
    }

    .credits-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: var(--gradient-credits);
    }

    .credits-card-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-md);
      flex-wrap: wrap;
    }

    .credits-info {
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
    }

    .credits-label {
      display: flex;
      align-items: center;
      gap: var(--space-xs);
      font-size: 0.8125rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--color-text-muted);
    }

    .credits-total {
      font-size: 2.5rem;
      font-weight: 800;
      letter-spacing: -0.04em;
      background: var(--gradient-credits);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      line-height: 1;
    }

    .credits-breakdown {
      display: flex;
      gap: var(--space-md);
      margin-top: var(--space-xs);
      flex-wrap: wrap;
    }

    .credits-breakdown-item {
      font-size: 0.8125rem;
      color: var(--color-text-muted);
    }

    .credits-breakdown-item strong {
      color: var(--color-text);
      font-weight: 600;
    }

    .credits-email {
      font-size: 0.875rem;
      color: var(--color-text-muted);
      padding: var(--space-sm) var(--space-md);
      border-radius: var(--radius-sm);
      background: rgb(255 255 255 / 4%);
      border: 1px solid var(--color-border);
      font-weight: 500;
      word-break: break-all;
    }

    .nav-grid {
      display: grid;
      gap: var(--space-md);
      grid-template-columns: repeat(2, 1fr);
    }

    .nav-btn {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-sm);
      padding: var(--space-lg) var(--space-xl);
      border-radius: var(--radius-lg);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      cursor: pointer;
      text-align: left;
      text-decoration: none;
      color: var(--color-text);
      transition:
        border-color var(--duration-normal) var(--ease-out),
        transform var(--duration-normal) var(--ease-out),
        box-shadow var(--duration-normal) var(--ease-out),
        background var(--duration-normal) var(--ease-out);
      overflow: hidden;
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }

    .nav-btn::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--gradient-primary);
      opacity: 0.6;
      transition: opacity var(--duration-normal) var(--ease-out);
    }

    .nav-btn:hover {
      border-color: var(--color-border-glow);
      transform: translateY(-3px);
      box-shadow: var(--shadow-glow-primary);
      background: rgb(255 255 255 / 3%);
    }

    .nav-btn:hover::before {
      opacity: 1;
    }

    .nav-btn:active {
      transform: translateY(-1px);
    }

    .nav-btn--credits::before {
      background: var(--gradient-credits);
      opacity: 0.8;
    }

    .nav-btn--profile::before {
      background: linear-gradient(90deg, var(--palette-violet-400), var(--palette-violet-600));
      opacity: 0.7;
    }

    .nav-btn-title {
      font-size: 1rem;
      font-weight: 700;
      letter-spacing: -0.02em;
      color: var(--color-text);
    }

    .nav-btn-desc {
      font-size: 0.8125rem;
      color: var(--color-text-muted);
      line-height: 1.4;
    }

    .nav-btn-arrow {
      position: absolute;
      top: var(--space-md);
      right: var(--space-md);
      font-size: 1rem;
      opacity: 0.3;
      transition: opacity var(--duration-normal) var(--ease-out), transform var(--duration-normal) var(--ease-out);
    }

    .nav-btn:hover .nav-btn-arrow {
      opacity: 0.7;
      transform: translate(2px, -2px);
    }

    .loading-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--space-md);
      padding: var(--space-2xl) 0;
      color: var(--color-text-muted);
    }

    .error-state {
      padding: var(--space-lg);
      border-radius: var(--radius);
      background: rgb(244 63 94 / 8%);
      border: 1px solid rgb(251 113 133 / 25%);
      color: var(--color-error);
      text-align: center;
    }

    @media (max-width: 639px) {
      .nav-grid {
        grid-template-columns: 1fr;
      }

      .credits-card-inner {
        flex-direction: column;
        align-items: flex-start;
      }
    }
    `,
  ];

  @state() private user: UserMe | null = null;
  @state() private loading = true;
  @state() private error = '';

  connectedCallback() {
    super.connectedCallback();
    void this.loadUser();
  }

  private async loadUser() {
    this.loading = true;
    this.error = '';

    try {
      this.user = await getMe();
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        return;
      }
      this.error =
        err instanceof ApiClientError ? err.message : 'Error al cargar el perfil';
    } finally {
      this.loading = false;
    }
  }

  private go(path: string) {
    void navigate(path);
  }

  render() {
    if (this.loading) {
      return html`
        <div class="loading-state animate-fade-up">
          <div class="spinner" role="status" aria-label="Cargando"></div>
          <p>Cargando dashboard…</p>
        </div>
      `;
    }

    if (this.error) {
      return html`<div class="error-state">${this.error}</div>`;
    }

    if (!this.user) {
      return html``;
    }

    const totalCredits = (this.user.credits ?? 0) + (this.user.extraCredits ?? 0);

    return html`
      <div class="animate-fade-up">
        <section class="hero">
          <div class="hero-content">
            <span class="hero-eyebrow">✦ Panel de control</span>
            <h1>Dashboard</h1>
            <p class="subtitle">
              Bienvenido de nuevo. Gestiona tu contenido y créditos desde aquí.
            </p>
          </div>
        </section>

        <div class="credits-card">
          <div class="credits-card-inner">
            <div class="credits-info">
              <span class="credits-label">
                <span aria-hidden="true">⚡</span>
                Créditos totales disponibles
              </span>
              <span class="credits-total">${totalCredits}</span>
              <div class="credits-breakdown">
                <span class="credits-breakdown-item">
                  Suscripción: <strong>${this.user.credits}</strong>
                </span>
                <span class="credits-breakdown-item">
                  Extra: <strong>${this.user.extraCredits}</strong>
                </span>
              </div>
            </div>
            <span class="credits-email">${this.user.email}</span>
          </div>
        </div>

        <div class="nav-grid">
          <button class="nav-btn" @click=${() => this.go('/content')}>
            <span class="nav-btn-arrow" aria-hidden="true">↗</span>
            <span class="nav-btn-title">Contenido</span>
            <span class="nav-btn-desc">Crea y gestiona tus generaciones de contenido.</span>
          </button>

          <button class="nav-btn" @click=${() => this.go('/campaigns')}>
            <span class="nav-btn-arrow" aria-hidden="true">↗</span>
            <span class="nav-btn-title">Campañas</span>
            <span class="nav-btn-desc">Diseña campañas multicanal con roadmap estratégico.</span>
          </button>

          <button class="nav-btn nav-btn--profile" @click=${() => this.go('/profile')}>
            <span class="nav-btn-arrow" aria-hidden="true">↗</span>
            <span class="nav-btn-title">Mi Perfil</span>
            <span class="nav-btn-desc">Consulta y edita tus datos de cuenta.</span>
          </button>

          <button class="nav-btn nav-btn--credits" @click=${() => this.go('/credits')}>
            <span class="nav-btn-arrow" aria-hidden="true">↗</span>
            <span class="nav-btn-title">Créditos</span>
            <span class="nav-btn-desc">Compra créditos o gestiona tu suscripción.</span>
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dashboard-page': DashboardPage;
  }
}
